/**
 * DueSoonRail — shared aspect. The "due soon" right-rail sidebar:
 * amber kicker, Cormorant title, subtitle + count pill, list of
 * urgency-colored deadline rows (circular days-left badge +
 * primary text + meta line), empty state, and a "view all" footer
 * link with optional overflow count.
 *
 * No card wrapper — rows sit directly on the surrounding hero bg,
 * separated only by hairlines.
 *
 * Consumer pre-filters / slices items and pre-formats metaText.
 * The aspect owns urgency tier coloring (theme.accent / success /
 * textOnSurface), badge geometry, hover state, and footer overflow
 * math.
 */
import { useState } from "react";
import { RADII, SPACING, STROKES } from "../../lib/brand";
import {
  Platform,
  Pressable,
  Text,
  View,
  type ViewStyle,
} from "react-native";

import type { PaneTheme } from "../brand/paneThemes";

export type DueSoonRailItem = {
  key: string;
  /** Whole-day count (0+). Drives badge color + text. */
  daysUntil: number;
  primaryText: string;
  metaText: string;
  onPress?: () => void;
  accessibilityLabel?: string;
};

export type DueSoonRailProps = {
  theme: PaneTheme;
  items: DueSoonRailItem[];
  /** Total count BEFORE slicing — drives the count pill + footer overflow. */
  totalCount?: number;
  kicker?: string;
  title?: string;
  subtitle?: string;
  emptyText?: string;
  viewAllLabel?: string;
  onPressViewAll?: () => void;
};

export function DueSoonRail({
  theme,
  items,
  totalCount,
  kicker = "Due soon",
  title = "Upcoming deliverables",
  subtitle = "Next 14 days",
  emptyText = "You're all caught up — no deliverables in this window.",
  viewAllLabel = "View all",
  onPressViewAll,
}: DueSoonRailProps) {
  const pillCount = totalCount ?? items.length;
  const overflow = totalCount != null ? Math.max(0, totalCount - items.length) : 0;
  const showFooter = onPressViewAll != null && (items.length > 0 || overflow > 0);
  return (
    <View>
      {/* Kicker */}
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <View style={{ width: 18, height: 1, backgroundColor: theme.accent }} />
        <Text
          style={{
            fontFamily: theme.bodyBoldFont,
            fontSize: 9.5,
            letterSpacing: 2.1,
            textTransform: "uppercase",
            color: theme.accent,
          }}
        >
          {kicker}
        </Text>
      </View>

      {/* Title */}
      <Text
        style={{
          fontFamily: theme.displayMediumFont,
          fontSize: 20,
          lineHeight: 24,
          color: theme.textOnSurface,
          marginBottom: 2,
        }}
      >
        {title}
      </Text>

      {/* Subtitle + count pill */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 11,
            color: theme.textOnSurfaceDim,
          }}
        >
          {subtitle}
        </Text>
        {pillCount > 0 ? (
          <View
            style={{
              paddingHorizontal: 6,
              paddingVertical: 1,
              borderRadius: RADII.full,
              backgroundColor: theme.accentTintSoft,
            }}
          >
            <Text
              style={{
                fontFamily: theme.bodyBoldFont,
                fontSize: 10,
                color: theme.accent,
              }}
            >
              {pillCount}
            </Text>
          </View>
        ) : null}
      </View>

      {/* Rows */}
      <View>
        {items.length === 0 ? (
          <View style={{ paddingVertical: 28, alignItems: "center" }}>
            <Text
              style={{
                textAlign: "center",
                fontStyle: "italic",
                fontFamily: theme.bodyFont,
                fontSize: 12.5,
                lineHeight: 18,
                color: theme.textOnSurfaceDim,
                maxWidth: 280,
              }}
            >
              {emptyText}
            </Text>
          </View>
        ) : (
          items.map((item, i) => (
            <DueSoonRow
              key={item.key}
              item={item}
              isLast={i === items.length - 1}
              theme={theme}
            />
          ))
        )}
      </View>

      {/* Footer */}
      {showFooter ? (
        <Pressable
          onPress={onPressViewAll}
          accessibilityRole="button"
          accessibilityLabel={viewAllLabel}
          style={[
            {
              alignSelf: "flex-end",
              marginTop: 10,
              paddingVertical: 6,
              paddingHorizontal: SPACING.xxs,
            },
            Platform.OS === "web"
              ? ({ cursor: "pointer" } as unknown as ViewStyle)
              : null,
          ]}
        >
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: theme.accent,
            }}
          >
            {viewAllLabel +
              (overflow > 0 ? " (" + overflow + " more)" : "") +
              " →"}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

function DueSoonRow({
  item,
  isLast,
  theme,
}: {
  item: DueSoonRailItem;
  isLast: boolean;
  theme: PaneTheme;
}) {
  const [hover, setHover] = useState(false);
  const isWeb = Platform.OS === "web";

  // Urgency tier.
  const isUrgent = item.daysUntil < 3;
  const isSoon = !isUrgent && item.daysUntil <= 7;
  const urgencyColor = isUrgent
    ? theme.accent
    : isSoon
      ? theme.success
      : theme.textOnSurface;
  const badgeBg = isUrgent
    ? theme.accentTintSoft
    : isSoon
      ? theme.successTintSoft
      : "rgba(28,43,26,0.05)";
  const badgeBorder = isUrgent
    ? theme.accentBorderHover
    : isSoon
      ? theme.successBorderSoft
      : "rgba(28,43,26,0.25)";

  return (
    <Pressable
      onPress={item.onPress}
      disabled={!item.onPress}
      accessibilityRole={item.onPress ? "link" : undefined}
      accessibilityLabel={item.accessibilityLabel}
      {...(isWeb
        ? ({
            onMouseEnter: () => setHover(true),
            onMouseLeave: () => setHover(false),
          } as const)
        : {})}
      style={[
        {
          paddingVertical: SPACING.sm,
          paddingHorizontal: SPACING.xxs,
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
          borderBottomWidth: isLast ? 0 : 1,
          borderBottomColor: theme.border,
          backgroundColor:
            hover && isWeb && item.onPress
              ? "rgba(28,43,26,0.04)"
              : "transparent",
          minHeight: 60,
        },
        isWeb && item.onPress
          ? ({
              cursor: "pointer",
              transition: "background-color 0.15s",
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      <View
        style={{
          width: 32,
          height: 32,
          borderRadius: RADII.full,
          borderWidth: STROKES.hairline,
          borderColor: badgeBorder,
          backgroundColor: badgeBg,
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Text
          style={{
            fontFamily: theme.bodyBoldFont,
            fontSize: 13,
            color: urgencyColor,
            includeFontPadding: false,
            textAlign: "center",
          }}
        >
          {item.daysUntil}
        </Text>
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          numberOfLines={1}
          style={{
            fontFamily: theme.bodyBoldFont,
            fontSize: 13.5,
            lineHeight: 18,
            color: theme.textOnSurface,
          }}
        >
          {item.primaryText}
        </Text>
        <Text
          numberOfLines={1}
          style={{
            marginTop: 2,
            fontFamily: theme.bodyFont,
            fontSize: 11,
            lineHeight: 15,
            color: theme.textOnSurfaceDim,
            letterSpacing: 0.2,
            fontVariant: ["tabular-nums"],
          }}
        >
          {item.metaText}
        </Text>
      </View>
    </Pressable>
  );
}
